import React from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import District from '../../model/District';

type AttendanceLayoutConfig = {
	rowsPerPage: number;
	rowHeight: number;
	separatorRowHeight: number;
};

type Props = {
	open: boolean;
	onClose: () => void;
	onConfirmDownload: () => void;
	selectedAttendanceDistrict: string;
	onAttendanceDistrictChange: (event: SelectChangeEvent<string>) => void;
	eligibleCodeDistricts: District[];
	attendanceLayoutConfig: AttendanceLayoutConfig;
	onAttendanceLayoutConfigChange: (next: AttendanceLayoutConfig) => void;
	isCodesDownloading: boolean;
	isAttendanceDownloading: boolean;
	isCodesGenerating: boolean;
};

const Stage3AttendanceDialog = ({
	open,
	onClose,
	onConfirmDownload,
	selectedAttendanceDistrict,
	onAttendanceDistrictChange,
	eligibleCodeDistricts,
	attendanceLayoutConfig,
	onAttendanceLayoutConfigChange,
	isCodesDownloading,
	isAttendanceDownloading,
	isCodesGenerating,
}: Props) => (
	<Dialog
		open={open}
		onClose={onClose}
		fullWidth
		maxWidth='sm'
	>
		<DialogTitle>Generowanie listy obecności</DialogTitle>
		<DialogContent>
			<FormControl
				fullWidth
				sx={{ mt: 1 }}
			>
				<InputLabel id='attendance-district-label'>Okręg</InputLabel>
				<Select
					labelId='attendance-district-label'
					value={selectedAttendanceDistrict}
					label='Okręg'
					onChange={onAttendanceDistrictChange}
				>
					<MenuItem value='all'>Wszystkie okręgi (jedno archiwum ZIP)</MenuItem>
					{eligibleCodeDistricts.map((district: District) => (
						<MenuItem
							key={district.id}
							value={String(district.id)}
						>
							{district.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			<Grid
				container
				spacing={1}
				sx={{ mt: 1 }}
			>
				<Grid
					item
					xs={12}
				>
					<Typography variant='subtitle2'>Ustawienia układu wydruku</Typography>
				</Grid>
				<Grid
					item
					xs={4}
				>
					<TextField
						fullWidth
						type='number'
						label='Wierszy na stronę'
						value={attendanceLayoutConfig.rowsPerPage}
						onChange={(e) =>
							onAttendanceLayoutConfigChange({
								...attendanceLayoutConfig,
								rowsPerPage: Number(e.target.value || 30),
							})
						}
					/>
				</Grid>
				<Grid
					item
					xs={4}
				>
					<TextField
						fullWidth
						type='number'
						label='Wysokość wiersza'
						value={attendanceLayoutConfig.rowHeight}
						onChange={(e) =>
							onAttendanceLayoutConfigChange({
								...attendanceLayoutConfig,
								rowHeight: Number(e.target.value || 30),
							})
						}
					/>
				</Grid>
				<Grid
					item
					xs={4}
				>
					<TextField
						fullWidth
						type='number'
						label='Wys. linii rozdzielającej'
						value={attendanceLayoutConfig.separatorRowHeight}
						onChange={(e) =>
							onAttendanceLayoutConfigChange({
								...attendanceLayoutConfig,
								separatorRowHeight: Number(e.target.value || 0),
							})
						}
					/>
				</Grid>
			</Grid>

			<Typography
				variant='caption'
				sx={{ mt: 1, display: 'block' }}
			>
				Ustawienia zapisywane lokalnie w przeglądarce. Domyślnie: 30 wierszy, wysokość 30, linia rozdzielająca 0.
			</Typography>

			<Typography
				variant='body2'
				sx={{ mt: 2 }}
			>
				{selectedAttendanceDistrict === 'all'
					? `Wygeneruje ZIP z osobnym plikiem listy obecności dla każdego okręgu (${eligibleCodeDistricts.length} plików).`
					: 'Wygeneruje pojedynczy plik XLSX listy obecności dla wybranego okręgu.'}
			</Typography>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose}>Anuluj</Button>
			<Button
				variant='contained'
				onClick={onConfirmDownload}
				disabled={isCodesDownloading || isAttendanceDownloading || isCodesGenerating}
			>
				Generuj i pobierz
			</Button>
		</DialogActions>
	</Dialog>
);

export default Stage3AttendanceDialog;
